const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const SyncError = require('@/lib/Syncing/SyncError');

/**
 * We now have multiple 'Syncers' instead of one class that handles pulling all data from the DT API.
 * Each syncer handles downloading certain things and they can all be run separately from each other.
 */
class AbstractSyncer {
    /**
     * @param $injector
     * @param {DataStore} dataStore
     * @param {EventBus} eventBus
     * @param {LocalStorageDataStore} localStorageDataStore
     * @param {SyncStateManager} syncStateManager
     */
    constructor(
        $injector,
        dataStore,
        eventBus,
        localStorageDataStore,
        syncStateManager
    ) {
        this.$injector = $injector;
        this.dataStore = dataStore;
        this.eventBus = eventBus;
        this.localStorageDataStore = localStorageDataStore;
        this.syncStateManager = syncStateManager;

        /**
         * @type {ConsoleLogger}
         */
        this.log = logFactory('Syncer');
    }

    /* istanbul ignore next */
    /**
     * @abstract
     *
     * Return an array of IndexedDB table names this syncer managed.
     * When clear() is called, these tables will be emptied.
     *
     * @return {string[]}
     */
    getManagedTableNames() {
        return [];
    }

    /* istanbul ignore next */
    /**
     * @abstract
     * @protected
     *
     * @return {{name: string, func: (function(Branch, {}): Promise), parallel?: boolean, verb?: string}[]}
     */
    getSyncFunctions() {
        return [];
    }

    /* istanbul ignore next */
    /**
     * @abstract
     * Does this syncer require a branch to be provided to do the needful?
     * If true, and no branch is given when 'run' is called it will throw an error.
     *
     * @return {boolean}
     */
    requiresBranch() {
        return false;
    }

    /**
     * @protected
     * Do something after a sync has finished (regardless of if new data was downloaded).
     * Maybe fire an event to let things know something changed.
     */
    onSyncFinish() {
        // Does nothing by default. Subclasses can implement this method to do something.
    }

    /**
     * Remove all the data that is managed by this syncer.
     *
     * @return {Promise}
     */
    async clear() {
        return Promise.all(
            this.getManagedTableNames().map((tableName) => {
                // Remove the metadata about the last sync of this table from localstorage.
                this.log.debug('Clearing last sync for', tableName);
                this.syncStateManager.remove(tableName);

                // Remove the actual data stored in IndexedDB for this table.
                this.log.debug('Clearing IndexedDB table', tableName);
                return this.dataStore.clear(tableName);
            }),
        );
    }

    /**
     * @protected
     *
     * @param {string} tableName
     * @param {string} repositoryName
     *
     * @return {{name: string, func: (function(Branch, {}): Promise), parallel?: boolean, verb?: string}}
     */
    createSyncRepositoryFunction(tableName, repositoryName) {
        const self = this;

        return {
            name: tableName,
            verb: 'LOADING',
            parallel: true,
            func: (branch) => {
                /**
                 * Construct the repository so we can perform the sync.
                 *
                 * @type {AbstractRepository}
                 */
                const repository = self.$injector.get(repositoryName);

                return repository.sync(branch ? branch.ID : null);
            },
        };
    }

    /**
     * Executes all the functions provided in the array as the first parameter.
     * Each one contains a function to execute and some extra data about how to execute that function.
     * Each function will do some fetching and/or processing of data and return a promise. When called, it will receive
     * the current branch as the first parameter and its entry from the array of itemsToSync as the second parameter.
     * The 'parallel' property in this array can be used to change the order in which the function executes. Any
     * entries will parallel: true will be executed first and at the same time. Once all of those are finished, the
     * non-parallel ones will execute one-by-one in the order they were defined in the array.
     *
     * Returns a promise which resolves when all of the functions have completed.
     *
     * The onProgress function will be called after each function and can be used to update the UI.
     *
     * @param {Branch|null} branch
     * @param {Function} [onProgress] A function which will be called after each item has finished syncing to monitor
     *     the progress.
     */
    async run(branch, onProgress) {
        if (this.requiresBranch() && !branch) {
            throw new Error('A branch must be provided to run this sync.');
        }

        const branchID = branch ? branch.ID : null;
        const startedAt = new Date();
        const syncFunctions = this.getSyncFunctions();
        this.log.log(
            `▶ Starting sync for ${syncFunctions.length} items`
            + (branchID ? ` for branch ${branchID}` : '')
            + ` at ${startedAt.toISOString()}`
        );

        // eslint-disable-next-line no-async-promise-executor
        return new Promise(async (resolve, reject) => {
            // Ensure each item has all the optional properties.
            syncFunctions.forEach((entry) => {
                entry.finished = false;
                entry.parallel = !!entry.parallel;
                entry.verb = entry.verb || 'LOADING';
            });

            /**
             * @param arr {{name: string, func: (function(Branch, {}): Promise), parallel?: boolean, verb?: string}[]}
             *
             * @return {{name: string, func: (function(Branch, {}): Promise), parallel?: boolean, verb?: string}[]}
             */
            const getUnfinishedEntries = (arr) => arr.filter((entry) => entry.finished === false);

            /**
             * @param arr {{name: string, func: (function(Branch, {}): Promise), parallel?: boolean, verb?: string}[]}
             *
             * @return {string[]}
             */
            const getEntryNames = (arr) => arr.map((entry) => entry.name);

            const updateProgress = (lastFinishedEntry) => {
                let progressKeys = getEntryNames(syncFunctions);
                let totalItems = progressKeys.length;
                let unfinishedEntries = getUnfinishedEntries(syncFunctions);

                if (typeof onProgress === 'function') {
                    let currentItem = null;
                    if (unfinishedEntries[0]) {
                        currentItem = {
                            name: unfinishedEntries[0].name,
                            verb: unfinishedEntries[0].verb,
                        };
                    }

                    let lastItem = null;
                    if (lastFinishedEntry) {
                        lastItem = {
                            name: lastFinishedEntry.name,
                            verb: lastFinishedEntry.verb,
                        };
                    }

                    onProgress({
                        // The Logger has the name, so use that instead of a convoluted solution.
                        syncerName: this.log.name,
                        totalItems,
                        completeItems: totalItems - unfinishedEntries.length,
                        // Pick a random item from the unfinished items to show as what's loading.
                        currentItem,
                        lastItem,
                    });
                }

                if (unfinishedEntries.length < 1) {
                    // Sync is finished.
                    let duration = ((new Date()).getTime() - startedAt.getTime()) / 1000;
                    this.log.log(
                        `🏁 Sync for ${syncFunctions.length} items`
                        + (branchID ? ` for branch ${branchID}` : '')
                        + ` completed in ${duration} seconds.`
                    );

                    this.onSyncFinish();

                    resolve();
                }
            };

            /**
             * @param {{name: string, func: (function(Branch, {}): Promise), parallel?: boolean, verb?: string}} entry
             * @return {Promise}
             */
            const executeEntry = (entry) => { // eslint-disable-line consistent-return
                return this.executeOneSyncFunction(branch, entry)
                    .then(() => {
                        entry.finished = true;
                        updateProgress(entry);
                    })
                    .catch((error) => {
                        this.log.error(`Syncing ${entry.name} failed.`, error);

                        let errorMessage;
                        try {
                            if (error instanceof Error) {
                                errorMessage = error.toString();
                            } else {
                                errorMessage = (error && typeof error === 'object')
                                    ? JSON.stringify(error)
                                    : error;
                            }
                        } catch (e) {
                            // Just in case toString fails.
                        }

                        reject(new SyncError(errorMessage, entry.name));
                    });
            };

            // Call the progress listener initially before we start syncing.
            updateProgress(null);

            // First process all the parallel entries.
            const parallelEntries = syncFunctions.filter((entry) => entry.parallel === true);
            await Promise.all(
                parallelEntries.map((entry) => executeEntry(entry)),
            );

            // Now process all the sequential entries one by one.
            const sequentialEntries = syncFunctions.filter((entry) => !entry.parallel);
            for (let i = 0; i < sequentialEntries.length; i++) {
                await executeEntry(sequentialEntries[i]);
            }
        });
    }

    /**
     * @private
     *
     * @param {Branch|null} branch
     * @param {{name: string, func: (function(Branch, {}): Promise), parallel?: boolean, verb?: string}} entry
     *
     * @return {Promise}
     */
    async executeOneSyncFunction(branch, entry) {
        // Check if this entry is already synced.
        // We determine that by the presence of a "lastSynced.{tableName}" entry in localstorage with
        // a matching branchID.
        const branchID = branch ? branch.ID : null;
        const syncedAt = this.syncStateManager.isSynced(entry.name, branchID);
        if (syncedAt) {
            // Already synced. Nothing to do.
            this.log.log(`✅ ${entry.name} already synced for branch`, branchID, 'at', syncedAt.toISOString());
            return true;
        }

        // This table needs to be synced.
        entry.verb = 'SYNCING';
        this.log.log(`🔄 ${entry.name} needs syncing.`);

        return entry.func(branch, entry)
            .then((result) => {
                this.log.log(`🟢 ${entry.name} finished syncing.`);

                return result;
            });
    }
}

module.exports = AbstractSyncer;
